import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  IconButton,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import React, { useState } from "react";
import moment from "moment";
import { showError } from "../../toastify";

// Single entry point for every training-dates unlock change:
//  - employee not unlocked -> just asks for a reason, action "unlock"
//  - employee unlocked     -> lets the user choose "Remove unlock" or "Extend unlock"
//                             (defaults to Extend; extend also asks how many
//                             extra days to add, 1-3)
const TrainingUnlockReasonDialog = (props) => {
  const { open, employee, loading, onClose, onSubmit } = props;

  const isOverdue =
    !!employee?.isTrainingDatesUnlocked &&
    !!employee?.trnUnlockedEndDate &&
    moment().isAfter(moment(employee.trnUnlockedEndDate));

  // Whenever the employee is currently unlocked, let the user pick between
  // removing the unlock or extending it - regardless of whether it's overdue.
  const canChoose = !!employee?.isTrainingDatesUnlocked;

  const defaultAction = !employee?.isTrainingDatesUnlocked
    ? "unlock"
    : "extend";

  const [action, setAction] = useState(defaultAction);
  const [reason, setReason] = useState("");
  const [daysToAdd, setDaysToAdd] = useState(1);
  const [reasonHasError, setReasonHasError] = useState(false);

  const title = !employee?.isTrainingDatesUnlocked
    ? "Unlock training dates"
    : "Update training dates unlock";

  const handleSubmit = () => {
    if (!reason.trim()) {
      setReasonHasError(true);
      showError("Reason is required");
      return;
    }

    onSubmit(
      action === "extend"
        ? { action, daysToAdd, reason: reason.trim() }
        : { action, reason: reason.trim() }
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle className="text-primary">
        {title}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <span className="material-icons">close</span>
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {employee && (
          <p className="mb-2">
            <strong>
              {employee.lastName} {employee.firstName}
            </strong>
            {isOverdue && (
              <>
                {" "}
                <small className="text-danger">
                  (unlock overdue since{" "}
                  {moment(employee.trnUnlockedEndDate).format("ll LT")})
                </small>
              </>
            )}
          </p>
        )}

        {canChoose && (
          <FormControl className="mb-2">
            <FormLabel id="unlock-action-label">Choose an action</FormLabel>
            <RadioGroup
              aria-labelledby="unlock-action-label"
              value={action}
              onChange={(e) => setAction(e.target.value)}
            >
              <FormControlLabel
                value="lock"
                control={<Radio size="small" />}
                label="Remove unlock"
              />
              <FormControlLabel
                value="extend"
                control={<Radio size="small" />}
                label="Extend unlock"
              />
            </RadioGroup>
          </FormControl>
        )}

        {action === "extend" && (
          <TextField
            select
            fullWidth
            size="small"
            label="Extra days to add"
            value={daysToAdd}
            onChange={(e) => setDaysToAdd(+e.target.value)}
            className="mb-3"
          >
            <MenuItem value={1}>1 day</MenuItem>
            <MenuItem value={2}>2 days</MenuItem>
            <MenuItem value={3}>3 days</MenuItem>
          </TextField>
        )}

        <TextField
          fullWidth
          multiline
          minRows={3}
          size="small"
          label="Reason"
          placeholder="Explain why you're taking this action"
          value={reason}
          error={reasonHasError && !reason.trim()}
          helperText={
            reasonHasError && !reason.trim() ? "Reason is required" : ""
          }
          onChange={(e) => {
            setReason(e.target.value);
            setReasonHasError(false);
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {loading ? "Please wait..." : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrainingUnlockReasonDialog;
